/**
 * AI Coach Chat API
 * 集成后端 Coach Chat API（4个接口）
 */

import service from '@/utils/https';
import i18n from '@/utils/i18n';
import { useUserStore } from '@/store/useUserStore';
import type { ApiResponse } from '@/types';
import type { ContextType, SendMessageRequest, ChatMessageResponseData, ChatHistoryResponseData, QuotaResponseData } from '@/types/aiChat';

export const STREAM_NOT_SUPPORTED_ERROR = 'STREAM_NOT_SUPPORTED';

type StreamEvent =
  | { type: 'start'; user_message_id: number }
  | { type: 'token'; content: string }
  | { type: 'end'; assistant_message_id: number; quota_used: number; quota_limit: number; remaining: number }
  | { type: 'error'; message: string };

type StreamCallbacks = {
  onStart?: (event: Extract<StreamEvent, { type: 'start' }>) => void;
  onToken: (content: string) => void;
  onEnd: (event: Extract<StreamEvent, { type: 'end' }>) => void;
  onError: (message: string) => void;
};

type ApiResponseOrData<T> = ApiResponse<T> | T;

const unwrapApiData = <T>(payload: ApiResponseOrData<T>): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
};

const getApiBaseUrl = (): string => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  return baseUrl.replace(/\/$/, '');
};

const buildApiUrl = (path: string): string => {
  const baseUrl = getApiBaseUrl();
  return baseUrl ? `${baseUrl}${path}` : path;
};

const buildStreamHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };

  const token = useUserStore.getState().token;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone) {
    headers['X-Client-Timezone'] = timezone;
  }

  const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || 'en';
  headers['Accept-Language'] = currentLanguage;

  return headers;
};

const stripAnsiSequences = (input: string): string => {
  let result = '';
  for (let i = 0; i < input.length; i += 1) {
    const code = input.charCodeAt(i);
    if (code === 27) {
      if (input[i + 1] === '[') {
        i += 2;
        while (i < input.length) {
          const finalByte = input.charCodeAt(i);
          if (finalByte >= 0x40 && finalByte <= 0x7e) {
            break;
          }
          i += 1;
        }
      }
      continue;
    }
    result += input[i];
  }
  return result;
};

const stripControlChars = (input: string): string => {
  let result = '';
  for (let i = 0; i < input.length; i += 1) {
    const code = input.charCodeAt(i);
    if (code === 0xfeff) continue;
    if (code < 32 || (code >= 127 && code <= 159)) continue;
    result += input[i];
  }
  return result;
};

const parseStreamLine = (line: string): StreamEvent | null => {
  const dataIndex = line.indexOf('data:');
  if (dataIndex === -1) return null;
  let payload = line.slice(dataIndex + 5);
  payload = stripAnsiSequences(payload);
  payload = stripControlChars(payload).trim();
  payload = payload.replace(/^(?:data:\s*)+/g, '').trim();
  if (!payload) return null;
  try {
    return JSON.parse(payload) as StreamEvent;
  } catch (error) {
    console.error('Failed to parse stream event:', payload, error);
    return null;
  }
};

/**
 * 发送消息到 AI Coach
 * POST /v1/coach-chat/{context_type}/{context_id}
 */
export const sendMessageToAI = async (contextType: ContextType, contextId: number, message: string): Promise<ChatMessageResponseData> => {
  const response = await service.post<ApiResponseOrData<ChatMessageResponseData>>(`/v1/coach-chat/${contextType}/${contextId}`, { message } as SendMessageRequest);
  return unwrapApiData(response.data);
};

/**
 * 流式发送消息到 AI Coach（SSE）
 * POST /v1/coach-chat/{context_type}/{context_id}/stream
 */
export const sendMessageToAIStream = async (contextType: ContextType, contextId: number, message: string, callbacks: StreamCallbacks, signal?: AbortSignal): Promise<void> => {
  const response = await fetch(buildApiUrl(`/v1/coach-chat/${contextType}/${contextId}/stream`), {
    method: 'POST',
    headers: buildStreamHeaders(),
    body: JSON.stringify({ message } as SendMessageRequest),
    signal,
  });

  if (!response.ok) {
    let errorMessage = 'Request failed';
    try {
      const errorData = (await response.json()) as { message?: string };
      errorMessage = errorData?.message || errorMessage;
    } catch (error) {
      console.error('Failed to parse stream error response:', error);
    }
    throw new Error(errorMessage);
  }

  if (!response.body || !response.body.getReader) {
    throw new Error(STREAM_NOT_SUPPORTED_ERROR);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() || '';

    for (const line of lines) {
      const event = parseStreamLine(line);
      if (!event) continue;

      switch (event.type) {
        case 'start':
          callbacks.onStart?.(event);
          break;
        case 'token':
          callbacks.onToken(event.content || '');
          break;
        case 'end':
          callbacks.onEnd(event);
          return;
        case 'error':
          callbacks.onError(event.message || 'Unknown error');
          throw new Error(event.message || 'Unknown error');
        default:
          break;
      }
    }
  }

  if (buffer) {
    const event = parseStreamLine(buffer);
    if (event?.type === 'end') {
      callbacks.onEnd(event);
    }
  }
};

/**
 * 获取聊天历史
 * GET /v1/coach-chat/{context_type}/{context_id}/history
 */
export const getChatHistory = async (contextType: ContextType, contextId: number): Promise<ChatHistoryResponseData> => {
  const response = await service.get<ApiResponseOrData<ChatHistoryResponseData>>(`/v1/coach-chat/${contextType}/${contextId}/history`);
  return unwrapApiData(response.data);
};

/**
 * 获取配额信息
 * GET /v1/coach-chat/{context_type}/{context_id}/quota
 */
export const getQuota = async (contextType: ContextType, contextId: number): Promise<QuotaResponseData> => {
  const response = await service.get<ApiResponseOrData<QuotaResponseData>>(`/v1/coach-chat/${contextType}/${contextId}/quota`);
  return unwrapApiData(response.data);
};

/**
 * 清空聊天历史
 * DELETE /v1/coach-chat/{context_type}/{context_id}
 */
export const clearChatHistory = async (contextType: ContextType, contextId: number): Promise<void> => {
  await service.delete(`/v1/coach-chat/${contextType}/${contextId}`);
};
